import { useMemo, type ComponentType } from 'react';
import type { User } from '@hero/schema';
import type { FeatureFlagKey } from '@hero/core';
import { useCurrentUser } from '@hero/hooks-shared';
import { useFeature, useFlagReady } from '@hero/web-context';
import { route as routeFn, type RouteName } from '@hero/web-hooks';
import {
  ApplicationsNavIcon,
  BillingIcon,
  ContactIcon,
  CustomersNavIcon,
  HandCoinsIcon,
  HomeNavIcon,
  type IconProps,
  LoadsNavIcon,
  MapPinnedIcon,
  MoreNavIcon,
  NewLoadsNavIcon,
  RouteIcon,
  SparklesIcon,
  UsersIcon,
  VehicleMainIcon,
  WalletIcon,
} from '@hero/web-icons';

/**
 * Non-generic alias around `route()` for call sites that resolve a
 * `RouteName` union at runtime (sidebar items, More cards). The
 * generic variadic signature on `route<T>` triggers a known
 * TypeScript 5.4 compiler crash when called with a union argument
 * (`Debug Failure. No error for last overload signature`); routing
 * through a `string`-typed wrapper sidesteps the bad inference path.
 */
const route = routeFn as unknown as (name: string) => string;

interface SidebarItem {
  title: string;
  icon: typeof HomeNavIcon;
  /**
   * Typed route name (resolved via `route()` at render time). Storing
   * the name instead of the URL keeps the sidebar tied to the
   * navigation registry — renaming a path in
   * `libs/frontend/web/hooks/src/navigation/routes/` updates every
   * link that points at it without a sweep here.
   */
  routeName: RouteName;
  featureFlag?: FeatureFlagKey;
}

const sidebarNavigation: SidebarItem[] = [
  { title: 'dashboard:Home.title', icon: HomeNavIcon, routeName: 'dashboard' },
  {
    title: 'm-agent:launcher.cta',
    icon: SparklesIcon,
    routeName: 'ai_agent',
    featureFlag: 'ai_agent_module',
  },
  {
    title: 'dashboard:NewLoads.title',
    icon: NewLoadsNavIcon,
    routeName: 'new_loads',
  },
  {
    title: 'dashboard:MyOrders.title',
    icon: LoadsNavIcon,
    routeName: 'orders',
    featureFlag: 'order_module',
  },
  {
    title: 'dashboard:CRM.title',
    icon: CustomersNavIcon,
    routeName: 'crm',
    featureFlag: 'crm_module',
  },
  {
    title: 'dashboard:Wallet.title',
    icon: WalletIcon,
    routeName: 'wallet',
    featureFlag: 'wallet_module',
  },
  {
    title: 'dashboard:Fleet.title',
    icon: VehicleMainIcon,
    routeName: 'fleet',
  },
  {
    title: 'm-financing:sidebar.title',
    icon: HandCoinsIcon,
    routeName: 'financing',
    featureFlag: 'financing_module',
  },
  {
    title: 'dashboard:Settings.title',
    icon: MoreNavIcon,
    routeName: 'settings',
    featureFlag: 'more_screen',
  },
];

/**
 * Cards rendered on the More page. Every card uses an `Icon` component
 * from `@hero/web-icons` so the page renders with one consistent icon
 * stroke ratio (`strokeWidth: 1.5` on a `viewBox 0 0 24 24`). The
 * legacy `img`-based path is retained for compat with older entries
 * but new cards must always pick `Icon`.
 *
 * Sign-out is intentionally NOT here — logout lives in the top-right
 * `<UserProfileMenu>` (with the explicit confirmation dialog) so the
 * More grid stays focused on navigation and a destructive action
 * doesn't sit alongside benign navigation cards.
 *
 * Translations live in `@hero/lang` under
 * `a-carrier-dashboard:more_page.<id>.{title,description}`. The
 * legacy `applications.json` namespace in `apps/carrier-dashboard/
 * public/locales` is deprecated; do not add new strings there.
 */
export interface ApplicationCard {
  id: number;
  title: string;
  description: string;
  /**
   * Typed `RouteName` resolved via `route()` at render time. Keeps
   * card data tied to the navigation registry; renaming a path in
   * `libs/frontend/web/hooks/src/navigation/routes/` updates every
   * card without a sweep here.
   */
  routeName: RouteName;
  img?: string;
  Icon?: ComponentType<IconProps>;
  /** Optional corner ribbon, e.g. an upcoming feature not yet live. */
  badge?: 'soon';
  /** Hide the card (and gate its route) behind a feature flag when set. */
  featureFlag?: FeatureFlagKey;
}

export const applicationsCardsData: ApplicationCard[] = [
  {
    id: 2,
    title: 'a-carrier-dashboard:more_page.users.title',
    description: 'a-carrier-dashboard:more_page.users.description',
    Icon: UsersIcon,
    routeName: 'settings_users',
  },
  {
    id: 5,
    title: 'a-carrier-dashboard:more_page.pricing.title',
    description: 'a-carrier-dashboard:more_page.pricing.description',
    Icon: RouteIcon,
    routeName: 'settings_pricing',
  },
  {
    id: 6,
    title: 'a-carrier-dashboard:more_page.contacts.title',
    description: 'a-carrier-dashboard:more_page.contacts.description',
    Icon: ContactIcon,
    routeName: 'settings_contacts',
  },
  {
    id: 7,
    title: 'a-carrier-dashboard:more_page.places.title',
    description: 'a-carrier-dashboard:more_page.places.description',
    Icon: MapPinnedIcon,
    routeName: 'settings_places',
  },
  {
    id: 8,
    title: 'a-carrier-dashboard:more_page.billing.title',
    description: 'a-carrier-dashboard:more_page.billing.description',
    Icon: BillingIcon,
    routeName: 'billing',
    featureFlag: 'billing_module',
  },
  {
    id: 9,
    title: 'a-carrier-dashboard:more_page.apps.title',
    description: 'a-carrier-dashboard:more_page.apps.description',
    Icon: ApplicationsNavIcon,
    routeName: 'settings_apps',
    badge: 'soon',
    featureFlag: 'apps_module',
  },
];

/**
 * Reactive sidebar hook — re-renders when feature flags or session
 * identity change. Driver gates come from `useCurrentUser().isDriver`
 * (session-scoped); do not re-derive from `user.roles`.
 */
export function useSidebarNavigation() {
  const { user, isDriver } = useCurrentUser();
  /** Published orders / new loads — same as home banner and `/new-loads` route. */
  const orderPublished = useFeature('order_module', 'sub_published_orders');
  /** Same resolution as `/my-orders` `<Feature subFlag="sub_my_orders" />` — hides nav when sub-flag is `hidden`. */
  const orderMyOrders = useFeature('order_module', 'sub_my_orders');
  const crm = useFeature('crm_module');
  const wallet = useFeature('wallet_module');
  const financing = useFeature('financing_module');
  const fleet = useFeature('fleet_module');
  const moreScreen = useFeature('more_screen');
  const aiAgent = useFeature('ai_agent_module', 'sub_full_page');
  /** Wait for a decisive PostHog answer before showing flag-gated nav items (avoids registry-default flash). */
  const orderFlagReady = useFlagReady('order_module');
  const crmFlagReady = useFlagReady('crm_module');
  const walletFlagReady = useFlagReady('wallet_module');
  const financingFlagReady = useFlagReady('financing_module');
  const fleetFlagReady = useFlagReady('fleet_module');
  const moreScreenFlagReady = useFlagReady('more_screen');
  const aiAgentFlagReady = useFlagReady('ai_agent_module');

  const flagReadyByKey: Record<string, boolean> = {
    order_module: orderFlagReady,
    crm_module: crmFlagReady,
    wallet_module: walletFlagReady,
    financing_module: financingFlagReady,
    more_screen: moreScreenFlagReady,
    ai_agent_module: aiAgentFlagReady,
  };

  const flags: Record<string, boolean> = {
    order_module: orderMyOrders.isEnabled,
    crm_module: crm.isEnabled,
    wallet_module: wallet.isEnabled,
    financing_module: financing.isEnabled,
    more_screen: moreScreen.isEnabled,
    ai_agent_module: aiAgent.isEnabled,
  };

  const isSidebarItemVisible = (item: SidebarItem) => {
    if (item.routeName === 'new_loads') {
      if (!orderFlagReady) return false;
      return orderPublished.isEnabled;
    }
    if (item.routeName === 'fleet') {
      if (!fleetFlagReady || !financingFlagReady) return false;
      if (!fleet.isEnabled || financing.isEnabled) return false;
      if (isDriver) return false;
      return true;
    }
    if (item.featureFlag) {
      if (!flagReadyByKey[item.featureFlag]) return false;
      return flags[item.featureFlag] ?? true;
    }
    return true;
  };

  return useMemo(() => {
    if (!user) return [];
    let items = sidebarNavigation;

    if (isDriver) {
      items = items.filter(item => item.routeName !== 'crm');
    }

    return items.filter(isSidebarItemVisible).map(item => ({
      title: item.title,
      icon: item.icon,
      url: route(item.routeName),
      featureFlag: item.featureFlag,
    }));
  }, [
    user,
    isDriver,
    orderPublished.isEnabled,
    orderFlagReady,
    crmFlagReady,
    walletFlagReady,
    financingFlagReady,
    fleetFlagReady,
    fleet.isEnabled,
    financing.isEnabled,
    moreScreenFlagReady,
    aiAgentFlagReady,
    flags.order_module,
    flags.crm_module,
    flags.wallet_module,
    flags.financing_module,
    flags.more_screen,
    flags.ai_agent_module,
  ]);
}

export const getApplicationsCardsData = (userData: User) => {
  return applicationsCardsData;
};
